import styled, { css } from "styled-components";
import { _ } from "@sablier/v2-mixins";
import type { ChangeEvent } from "react";

const Wrapper = styled.div`
  ${(props) => props.theme.styles.row}
`;

const after = css`
  &:after {
    position: absolute;
    top: 3px;
    left: 3px;
    content: "";
    display: block;
    width: calc(var(--size) - 2 * 3px - 2 * 2px);
    height: calc(var(--size) - 2 * 3px - 2 * 2px);
    border-radius: 50%;
    background: var(--ab, var(--border));
    transform: translateX(var(--x, 0));
    transition: transform var(--d-t, 0.3s) var(--d-t-e, ease),
      opacity var(--d-o, 0.2s), background 0.2s;
  }
`;
const checked = css`
  &:checked {
    --d-o: 0.3s;
    --d-t: 0.6s;
    --d-t-e: cubic-bezier(0.2, 0.85, 0.32, 1.2);
    --ab: var(--active-inner);
    --b: var(--background-active);
    --bc: var(--border-active);
    --x: calc(var(--size) * 1.8 - var(--size));
  }
`;
const focus = css`
  &:focus {
    box-shadow: 0 0 0 var(--focus);
  }
`;

const hover = css`
  &:hover:not(:checked) {
    --bc: var(--border-hover);
  }
`;

const BoxPartial = styled.div<{ size?: number | string }>`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  @supports (-webkit-appearance: none) or (-moz-appearance: none) {
    input[type="checkbox"] {
      --active: ${(props) => props.theme.colors.dark000};
      --active-inner: ${(props) => props.theme.colors.gray100};
      --focus: 2px ${(props) => props.theme.colors.inputBorder};
      --border: ${(props) => props.theme.colors.dark600};
      --border-active: ${(props) => props.theme.colors.inputBorder};
      --border-hover: ${(props) => props.theme.colors.inputBorder};
      --background: ${(props) => props.theme.colors.dark000};
      --background-active: ${(props) => props.theme.colors.dark700};
      --size: ${(props) => props.size || props.theme.sizes.switchPill};
      position: relative;
      display: inline-block;
      flex-shrink: 0;
      width: calc(var(--size) * 1.8);
      height: var(--size);
      margin: 0;
      vertical-align: top;
      border: 2px solid var(--bc, var(--border));
      border-radius: calc(var(--size) * 1 / 2);
      background: var(--b, var(--background));
      outline: none;
      cursor: pointer;
      appearance: none;
      transition: background 0.3s, border-color 0.3s, box-shadow 0.2s;

      + label {
        line-height: 20pt;
        cursor: pointer;
      }
    }
  }
`;

const Box = styled(BoxPartial)`
  @supports (-webkit-appearance: none) or (-moz-appearance: none) {
    input[type="checkbox"] {
      ${after}
      ${checked}
        ${focus}
        ${hover}
    }
  }
`;

const Input = styled.input.attrs({ type: "checkbox" })``;

const Label = styled.label`
  ${(props) => props.theme.styles.textElement}
  & {
    margin-left: 6px;
    color: ${(props) => props.theme.colors.gray400};
    text-align: left;
  }
`;

export interface Props {
  id: string;
  isChecked?: boolean;
  label?: string;
  onChange?: (e?: ChangeEvent<HTMLInputElement>) => void;
  size?: number;
}

function SwitchPill({
  id,
  isChecked = false,
  label,
  onChange = _.noop,
  size,
}: Props) {
  return (
    <Wrapper>
      <Box size={size && _.toSuffix(size, "px")}>
        <Input
          checked={isChecked}
          id={id}
          name={id}
          onChange={onChange}
          type={"checkbox"}
        />
        {!_.isNilOrEmptyString(label) && <Label>{label}</Label>}
      </Box>
    </Wrapper>
  );
}

export default SwitchPill;
